import { Button, Spin, Input, Card, Calendar, Select, Modal } from "antd";
import axios from "axios";
import { useState, useEffect } from "react";
import Sidebar from "../../GlobalFiles/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { API_BASE_URL } from "../../../../../api";
import { FileText, User, Calendar as CalendarIcon, Clock, Eye, Printer, SortAsc, Heart } from "lucide-react";
import { printTestReport } from "../../../../../helper/printTestReport";
import { useNavigate } from "react-router-dom";
import isTextTypeReport from "../../../../../helper/isTextTypeReport";

const PrintReport = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [currentModal, setCurrentModal] = useState(null);   // 'view' | 'print'
  const [selectedTestGroups, setSelectedTestGroups] = useState({});
  const [selectAllTests, setSelectAllTests] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterByDate, setFilterByDate] = useState(false);

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { data: { user }, } = useSelector((state) => state.auth);
  const isManager = user?.userType === "manager";

  useEffect(() => {
    if (!isManager) {
      navigate('/');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await fetchReports();
      setLoading(false);
    };
    loadData();
  }, [dispatch]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/reports?depth=2&status=completed`);
      setReports(response.data);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  const handleReportView = (report) => {
    // console.log("Viewing report:", report);
    setSelectedReport(report);
    setCurrentModal('view');
  }

  const showPrintModal = (report) => {
    setSelectedReport(report);
    setCurrentModal('print');
  }

  const filteredReports = reports.filter(report => {
    const query = searchQuery.toLowerCase();
    // console.log("selected date: ", new Date(selectedDate).toDateString());
    // console.log("date string: ", new Date(report.createdAt).toDateString() === new Date(selectedDate).toDateString());
    return (
      report.patient?.patientID?.toString().toLowerCase().includes(query) ||
      report.patientName?.toLowerCase().includes(query) ||
      report.testOrder?.testOrderID?.toString().toLowerCase().includes(query) ||
      report.date?.includes(query) ||
      report.tests?.some(test => test.name?.toLowerCase().includes(query))
    )
      && (
        !reportType ||
        (reportType === 'printed' && report.isPrinted) ||
        (reportType === 'unprinted' && !report.isPrinted) ||
        (reportType === 'all')
      )
      && ((filterByDate && selectedDate) ? (new Date(report.createdAt).toDateString() === new Date(selectedDate).toDateString()) : true);
  }) || [];

  // useEffect(() => {
  //   console.log("Selected Test Groups:", selectedTestGroups);
  // }, [filteredReports]);


  const groupByTestGroup = (results) => {
    const ans = results.reduce((groups, result) => {
      const group = result.test?.testGroup?.categoryName || 'Uncategorized';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(result);
      return groups;
    }, {});

    return ans;
  }


  useEffect(() => {
    const updateSorting = () => {
      if (!sortBy) return;
      const sorted = [...reports];
      switch (sortBy) {
        case 'dateAsc':
          sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          break;
        case 'dateDesc':
          sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'nameAsc':
          sorted.sort((a, b) => (a.patient?.patientName || '~').localeCompare(b.patient?.patientName || '~'));
          break;
        case 'nameDesc':
          sorted.sort((a, b) => (b.patient?.patientName || '~').localeCompare(a.patient?.patientName || '~'));
          break;
        default:
          return;
      }
      setReports(sorted);
    };
    updateSorting();
  }, [sortBy, reports]);


  const handlePrintReport = () => {
    printTestReport(selectedReport, selectedTestGroups);

    const resultIds = [];
    Object.values(selectedTestGroups).forEach(tests => {
      tests.forEach(test => {
        resultIds.push(test._id);
      });
    });

    setReports(prevReports => prevReports.map(report => {
      if (report._id === selectedReport._id) {
        return {
          ...report,
          isPrinted: report?.results?.every(r => resultIds.includes(r._id)) ? true : false,
          results: report.results.map(result => {
            if (resultIds.includes(result._id)) {
              return { ...result, isPrinted: true };
            }
            return result;
          }
          )
        };
      }
      return report;
    }));

    setSelectedReport(null);
    setCurrentModal(null);

    // console.log("Print process initiated for report:", selectedReport._id);
  };

  const getStatusColor = (order) => {
    const status = order?.status || (order?.results?.every(r => r.status === 'completed') ? 'completed' : 'pending');
    if (status === 'completed') return "bg-emerald-100 text-emerald-700";
    return "bg-blue-100 text-blue-700";
  };


  const getSortedByCategory = (results) => {
    if (!results) return [];
    return [...results].sort((a, b) => {
      const groupA = a.test?.testGroup?.categoryName || 'Uncategorized';
      const groupB = b.test?.testGroup?.categoryName || 'Uncategorized';
      return groupA.localeCompare(groupB);
    });
  }



  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar onCollapse={setSidebarCollapsed} />
      <div className={` flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'
        }`}>
        <div className="flex-1 p-6 ml-40">
          <Card className="mb-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-green-500 to-purple-600 rounded-xl">
                <Printer className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Print Reports</h1>
                <p className="text-gray-600">Print single or multiple reports of the same patient</p>
              </div>
              <div className="ml-auto flex items-center">
                <Input
                  allowClear
                  className="mr-4 p-3 w-80 border border-gray-300 rounded-lg"
                  placeholder="Search by id, name, number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Spin size="large" />
                <p className="mt-4 text-gray-600">Loading print report data...</p>
              </div>
            </div>
          ) :
            <div className="flex gap-4 h-[calc(100vh-200px)]">
              <div className="w-[30%] space-y-2 overflow-y-auto pr-2 max-h-full">
                {/* Selection Card */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-slate-700">Report Selection</h3>
                  </div>
                  <Select
                    allowClear
                    placeholder="Please select report type"
                    value={reportType}
                    className="w-full"
                    onChange={(val) => {
                      setReportType(val);
                    }}
                    options={[
                      {
                        value: 'printed',
                        label: 'Printed Reports',
                      },
                      {
                        value: 'unprinted',
                        label: 'Unprinted Reports',
                      },
                      {
                        value: 'all',
                        label: 'Show All Reports',
                      },
                    ]}
                    size="large"
                  />
                </div>

                {/* Sort Order Card */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                      <SortAsc className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-slate-700">Sort Order</h3>
                  </div>
                  <Select
                    className="w-full"
                    placeholder="Select sorting criteria"
                    value={sortBy}
                    onChange={(value) => setSortBy(value)}
                    options={[
                      { value: 'dateAsc', label: 'Date: Oldest to Newest' },
                      { value: 'dateDesc', label: 'Date: Newest to Oldest' },
                      { value: 'nameAsc', label: 'Name: A to Z' },
                      { value: 'nameDesc', label: 'Name: Z to A' },
                    ]}
                    size="large"
                  />
                </div>

                {/* Date Filter Card */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                      <CalendarIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-slate-700">Filter by Date</h3>
                  </div>
                  <Calendar
                    fullscreen={false}
                    className="w-full"
                    defaultValue={null}
                    headerRender={({ value, onChange }) => {
                      const current = value || (selectedDate ? selectedDate : new Date());
                      const currentDayjs = (typeof current?.format === "function") ? current : undefined;
                      const base = currentDayjs || null;
                      const currentYear = base ? base.year() : new Date().getFullYear();
                      const years = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);
                      const months = Array.from({ length: 12 }, (_, i) => i);

                      return (
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <Select
                            value={currentYear}
                            className="flex-1"
                            options={years.map((y) => ({ value: y, label: y }))}
                            onChange={(year) => {
                              if (!base) return;
                              onChange(base.year(year));
                            }}
                          />
                          <Select
                            value={base ? base.month() : 0}
                            className="flex-1"
                            options={months.map((m) => ({ value: m, label: new Date(2000, m, 1).toLocaleString("en-US", { month: "short" }) }))}
                            onChange={(month) => {
                              if (!base) return;
                              onChange(base.month(month));
                            }}
                          />
                        </div>
                      );
                    }}
                    onSelect={date => {
                      if (date && filterByDate && selectedDate && new Date(date).toDateString() === new Date(selectedDate).toDateString()) {
                        setFilterByDate(false);
                      } else {
                        setFilterByDate(true);
                      }
                      setSelectedDate(date);
                    }}
                    value={filterByDate ? selectedDate : null}
                  />
                </div>
              </div>

              <div className="w-[70%] bg-white rounded shadow p-4 flex flex-col max-h-full">
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                  <h1 className="font-semibold text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Reports ( Total: {filteredReports?.length || 0} )
                  </h1>
                </div>

                {(filteredReports?.length || 0) > 0 ? (
                  <div className="flex-1 space-y-3 overflow-y-auto pr-2">
                    {filteredReports?.map((report, index) => (
                      <div
                        key={report._id || index}
                        className={
                          `p-4 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-300 ${selectedReport && selectedReport._id === report._id
                            ? 'bg-blue-50 border-blue-500 shadow-md'
                            : 'bg-white hover:bg-gray-50'
                          }`}
                        onClick={() => showPrintModal(report)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-500" />
                                <span className="font-semibold text-gray-800">
                                  {report.patient?.patientName || 'Name not found'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                                  {report.testOrder?.testOrderID || report.testOrderID || 'N/A'}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                              <div className="flex items-center gap-1">
                                <CalendarIcon className="w-6 h-6" />
                                <span>{new Date(report.createdAt).toLocaleDateString() || 'No date'}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-6 h-6" />
                                <span>{new Date(report.createdAt).toLocaleTimeString() || 'No time'}</span>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-1">
                              {report.results && report.results.length > 0 ? (
                                report.results.slice(0, 3).map((result, resultIndex) => (
                                  <span
                                    key={resultIndex}
                                    className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                                  >
                                    {result?.test?.title || result?.test?.testName || result?.testName || result?.name || `Test ${resultIndex + 1}`}
                                  </span>
                                ))
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                  No tests
                                </span>
                              )}
                              {report.results && report.results.length > 3 && (
                                <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded-full text-xs">
                                  +{report.results.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col">
                            <div className="flex items-center gap-4 ml-4">
                              <Button
                                size="small"
                                type="text"
                                icon={<Eye className="w-6 h-6" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReportView(report);
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2"
                              />
                              <Button
                                size="small"
                                type="text"
                                icon={<Printer className="w-6 h-6" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showPrintModal(report);
                                }}
                                className="flex gap-1 items-center text-green-600 hover:text-green-700 hover:bg-green-50 p-2"
                              />
                            </div>
                            <div>
                              <span className={`mt-4 inline-block px-3 py-1 text-xs font-semibold rounded-full ${report.isPrinted ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                                {report.isPrinted ? 'Printed' : 'Not Printed'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-600 mb-2">No Reports Found</h3>
                    <p className="text-gray-500">
                      {searchQuery ? 'No reports match your search criteria.' : 'No reports available at the moment.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          }
        </div>
      </div>

      <Modal
        open={currentModal === 'print' && selectedReport !== null}
        title="Grouped Tests for Printing"
        onCancel={() => {
          setSelectedReport(null);
          setCurrentModal(null);
          setSelectedTestGroups({});
          setSelectAllTests(false);
        }}
        footer={null}
        width={800}
      >
        <div>
          {/* Grouped tests selectable list for printing */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-700">Select Tests to Print</h4>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={selectAllTests}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectAllTests(checked);
                      if (checked) {
                        setSelectedTestGroups(groupByTestGroup(selectedReport.results || []));
                      } else {
                        setSelectedTestGroups({});
                      }
                    }}
                  />
                  Select All
                </label>
                <Button
                  type="primary"
                  disabled={Object.keys(selectedTestGroups).length === 0}
                  onClick={handlePrintReport}>
                  Print Selected
                </Button>
              </div>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-100 rounded-md p-3">
              {selectedReport && Object.entries(groupByTestGroup(selectedReport.results || [])).map(([groupName, tests]) => (
                <div key={groupName} className="mb-2">
                  <label key={tests} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={groupName in selectedTestGroups}
                      onChange={(e) => {
                        const next = { ...selectedTestGroups };
                        if (e.target.checked) {
                          next[groupName] = tests;
                        } else {
                          delete next[groupName];
                        }
                        // lengths
                        setSelectAllTests(Object.keys(next).length === Object.keys(groupByTestGroup(selectedReport.results || [])).length);
                        setSelectedTestGroups(next);
                      }}
                    />
                    <div className="text-sm font-semibold text-gray-700 mb-2 flex flex-col">
                      {groupName}
                      <div className="flex gap-2 flex-wrap mt-1">
                        {tests.map((t) => {
                          return (
                            <div key={t?._id} className="font-md text-xs text-gray-800 bg-blue-300 px-2 rounded-lg">{t.test?.title || t.test?.testName || t.name || 'Unnamed Test'}</div>
                          )
                        })}
                      </div>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Report Details</h3>
              {/* <p className="text-sm text-gray-600">Complete report information</p> */}
            </div>
          </div>
        }
        open={currentModal === 'view' && selectedReport !== null}
        onCancel={() => {
          setSelectedReport(null);
          setCurrentModal(null);
        }}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setSelectedReport(null);
              setCurrentModal(null);
            }}
            className="bg-gray-100 hover:bg-gray-200 border-gray-300"
          >
            Close
          </Button>,
        ]}
        width={800}
        className="custom-modal"
      >
        {selectedReport && (
          <div className="space-y-6 pt-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900">Patient Information</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium text-gray-900">{selectedReport.patient?.patientName || selectedReport.patientName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Age:</span>
                    <span className="font-medium text-gray-900">{selectedReport.patient?.age || selectedReport.patientAge}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gender:</span>
                    <span className="font-medium text-gray-900">{selectedReport.patient?.gender || selectedReport.patientGender}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mobile:</span>
                    <span className="font-medium text-gray-900">{selectedReport.patient?.mobile || selectedReport.patientMobile}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-900">{selectedReport.patient?.email || selectedReport.email || "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Address:</span>
                    <span className="font-medium text-gray-900">{selectedReport.patient?.address || selectedReport.address || "N/A"}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-gray-900">Medical Information</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex justify-between">
                  <span className="text-gray-600">Doctor:</span>
                  <span className="font-medium text-gray-900">{selectedReport.doctor?.docName || selectedReport.docName || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Doctor Department:</span>
                  <span className="font-medium text-gray-900">{selectedReport.doctor?.department || selectedReport.docDepartment || "N/A"}</span>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Tests' Results Information</h4>
              </div>
              {selectedReport.results && selectedReport.results.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-purple-100">
                        <th className="text-left p-2 font-medium text-gray-700">Group</th>
                        <th className="text-left p-2 font-medium text-gray-700">Test Name</th>
                        <th className="text-left p-2 font-medium text-gray-700">Value</th>
                        <th className="text-left p-2 font-medium text-gray-700">Unit</th>
                        <th className="text-left p-2 font-medium text-gray-700">Normal</th>
                        <th className="text-left p-2 font-medium text-gray-700">Status</th>
                        <th className="text-left p-2 font-medium text-gray-700">Print Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedByCategory(selectedReport.results || []).map((res, index) => {
                        const category = res.test?.testGroup?.categoryName || 'Uncategorized';
                        const resCountInCategory = selectedReport.results.filter(t => (t.test?.testGroup?.categoryName || 'Uncategorized') === category).length;
                        const catIndex = getSortedByCategory(selectedReport.results || []).findIndex(t => (t.test?.testGroup?.categoryName || 'Uncategorized') === category);
                        // console.log("Category:", category, "CatIndex:", catIndex, "Index:", index, "ResCountInCategory:", resCountInCategory);
                        return (
                          <tr key={index} className="border-t border-purple-200">
                            {/* <td className="p-2 text-gray-900">{index + 1}</td> */}
                            {catIndex === index &&
                              <td className="p-2 text-gray-900" rowSpan={resCountInCategory}>{category || 'N/A'}</td>
                            }
                            <td className="p-2 text-gray-900">{res.test?.title || res.test?.testName || 'N/A'}</td>
                            <td className="p-2 text-gray-900" colSpan={isTextTypeReport(category) ? 3 : 1}>
                              {res.value || '-'}
                            </td>
                            {!isTextTypeReport(category) && (
                              <>
                                <td className="p-2 text-gray-900">{res.testUnit || '-'}</td>
                                <td className="p-2 text-gray-900">{res.normalValue || '-'}</td>
                              </>
                            )}
                            <td className={`p-2 text-gray-900 ${res.status === 'completed' ? 'text-green-500' : 'text-yellow-500'}`}>{res.status || '-'}</td>
                            {catIndex === index && (
                              <td className={`p-2 text-gray-900 font-semibold text-center ${res.isPrinted ? 'text-green-600' : 'text-red-600'}`} rowSpan={resCountInCategory}>{res.isPrinted ? 'Printed' : 'Not Printed'}</td>
                            )}
                          </tr>
                        );
                      }
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-600">No tests information available</p>
              )}
            </div>

            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-indigo-600" />
                <h4 className="font-semibold text-gray-900">Order Time</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                <div className="flex justify-between">
                  <span className="text-gray-600">Creation Date:</span>
                  <span className="font-medium text-gray-900">{selectedReport.createdAt ? new Date(selectedReport.createdAt).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-medium text-gray-900">{selectedReport.createdAt ? new Date(selectedReport.createdAt).toLocaleTimeString() : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium text-gray-900 ${getStatusColor(selectedReport)}`}>{selectedReport.status || (selectedReport.results?.every(r => r.status === 'completed') ? 'completed' : 'pending')}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
export default PrintReport;